import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Label } from '../../ui/label';
import { Loader2, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PlantDataStorageView = ({ formData, setFormData, selectedPlants = [], readOnly = false }) => {
  const [plants, setPlants] = useState([]);
  const [storageLocations, setStorageLocations] = useState({});
  const [loading, setLoading] = useState(true);

  // Check if plant_data_storage is in nested dictionary format (for viewing)
  const isNestedFormat = formData.plant_data_storage && 
                        typeof formData.plant_data_storage === 'object' && 
                        !Array.isArray(formData.plant_data_storage) &&
                        Object.keys(formData.plant_data_storage).some(key => key.startsWith('plant_'));

  // Initialize plant_data_storage as an array for editing
  const plantDataArray = isNestedFormat
    ? [] // Don't show array form when viewing nested data
    : Array.isArray(formData.plant_data_storage) 
      ? formData.plant_data_storage 
      : formData.plant_data_storage?.plant_id 
        ? [formData.plant_data_storage] // Convert old single plant to array
        : [];

  // Extract plant data from nested format for display
  const nestedPlantData = isNestedFormat 
    ? Object.entries(formData.plant_data_storage).map(([key, value]) => ({
        key,
        ...value
      }))
    : [];

  useEffect(() => {
    loadMasterData();
  }, []);

  // Load storage locations for pre-existing plants (for edit mode)
  useEffect(() => {
    if (plantDataArray && plantDataArray.length > 0) {
      plantDataArray.forEach(plantData => {
        if (plantData.plant_id) {
          loadStorageLocations(plantData.plant_id);
        }
      });
    }
  // eslint-disable-next-line
  }, [plantDataArray.length]); // Only run when number of plants changes

  const loadMasterData = async () => {
    try {
      const plantsRes = await axios.get(`${BACKEND_URL}/api/material-management/plants`);
      setPlants(plantsRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
      toast.error('Failed to load master data');
    } finally {
      setLoading(false);
    }
  };

  const loadStorageLocations = async (plantId) => {
    if (!plantId) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/material-management/storage-locations`, {
        params: { plant_id: plantId }
      });
      setStorageLocations(prev => ({
        ...prev,
        [plantId]: response.data || []
      }));
    } catch (error) {
      console.error('Error loading storage locations:', error);
      setStorageLocations(prev => ({
        ...prev,
        [plantId]: []
      }));
    }
  };

  const handleAddPlant = () => {
    const newPlantData = [
      ...plantDataArray,
      {
        plant_id: '',
        storage_location_id: '',
        period_ind_shelf_life: ''
      }
    ];
    setFormData({
      ...formData,
      plant_data_storage: newPlantData
    });
  };

  const handleRemovePlant = (index) => {
    const newPlantData = plantDataArray.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      plant_data_storage: newPlantData
    });
  };

  const handlePlantFieldChange = (index, field, value) => {
    const newPlantData = [...plantDataArray];
    newPlantData[index] = {
      ...newPlantData[index],
      [field]: value
    };
    
    // Load storage locations when plant changes
    if (field === 'plant_id' && value) {
      loadStorageLocations(value);
      // Reset storage location when plant changes
      newPlantData[index].storage_location_id = '';
    }
    
    setFormData({
      ...formData,
      plant_data_storage: newPlantData
    });
  };

  const getAvailablePlants = () => {
    if (selectedPlants.length > 0) {
      return plants.filter(p => selectedPlants.includes(p.plant_id));
    }
    return plants;
  };

  const availablePlants = getAvailablePlants();
  const plantConsistencyWarning = selectedPlants.length > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        <span className="ml-3 text-gray-600">Loading plant data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-3">
        <h3 className="text-lg font-semibold text-gray-900">Plant Data / Storage</h3>
        <p className="text-sm text-gray-600 mt-1">
          Configure plant-specific data and storage location information. You can add multiple plants.
        </p>
      </div>

      {/* Plant Consistency Warning */}
      {plantConsistencyWarning && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-orange-900">Plant Consistency Enforced</h4>
            <p className="text-sm text-orange-700 mt-1">
              Plant selection is restricted to plants already selected in other views ({selectedPlants.join(', ')}).
            </p>
          </div>
        </div>
      )}

      {/* Display Nested Plant Data (Read-Only for Viewing) */}
      {isNestedFormat && nestedPlantData.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-900">Plant Extensions</h4>
          {nestedPlantData.map((plantData, index) => (
            <div key={plantData.key} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h5 className="text-sm font-semibold text-orange-900">
                  Plant {plantData.plant_id}
                </h5>
                <span className="text-xs bg-orange-200 text-orange-800 px-2 py-1 rounded">
                  Extension {index + 1}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                {plantData.storage_location && (
                  <div>
                    <span className="font-medium text-gray-700">Storage Location:</span>
                    <p className="text-gray-900">{plantData.storage_location}</p>
                  </div>
                )}
                {plantData.period_ind_shelf_life && (
                  <div>
                    <span className="font-medium text-gray-700">Shelf Life Period:</span>
                    <p className="text-gray-900">{plantData.period_ind_shelf_life}</p>
                  </div>
                )}
                {plantData.cost_center && (
                  <div>
                    <span className="font-medium text-gray-700">Cost Center:</span>
                    <p className="text-gray-900">{plantData.cost_center}</p>
                  </div>
                )}
                {plantData.mrp_controller && (
                  <div>
                    <span className="font-medium text-gray-700">MRP Controller:</span>
                    <p className="text-gray-900">{plantData.mrp_controller}</p>
                  </div>
                )}
                {plantData.purchasing_group && (
                  <div>
                    <span className="font-medium text-gray-700">Purchasing Group:</span>
                    <p className="text-gray-900">{plantData.purchasing_group}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Plant Data Entries (Editable Array Format) */}
      {!isNestedFormat && plantDataArray.length > 0 ? (
        <div className="space-y-4">
          {plantDataArray.map((plantData, index) => {
            const plantStorageLocs = storageLocations[plantData.plant_id] || [];
            
            return (
              <div key={index} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-orange-900">
                    Plant Extension {index + 1}
                  </h4>
                  {plantDataArray.length > 1 && !readOnly && (
                    <button
                      type="button"
                      onClick={() => handleRemovePlant(index)}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Remove this plant"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Plant *</Label>
                    <select
                      className={`mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${readOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                      value={plantData.plant_id || ''}
                      onChange={(e) => handlePlantFieldChange(index, 'plant_id', e.target.value)}
                      disabled={readOnly}
                    >
                      <option value="">Select Plant</option>
                      {availablePlants.map(p => (
                        <option key={p.plant_id} value={p.plant_id}>
                          {p.plant_id} - {p.plant_name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Where the material is used or stored</p>
                    {availablePlants.length === 0 && selectedPlants.length > 0 && (
                      <p className="text-xs text-red-600 mt-1">No valid plants available. Please check plant selection in other views.</p>
                    )}
                  </div>
                  
                  <div>
                    <Label>Storage Location</Label>
                    <select
                      className={`mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${readOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                      value={plantData.storage_location_id || ''}
                      onChange={(e) => handlePlantFieldChange(index, 'storage_location_id', e.target.value)}
                      disabled={readOnly || !plantData.plant_id}
                    >
                      <option value="">
                        {plantData.plant_id ? 'Select Storage Location (Optional)' : 'Select Plant First'}
                      </option>
                      {plantStorageLocs.map(sl => (
                        <option key={sl.storage_location_id} value={sl.storage_location_id}>
                          {sl.storage_location_id} - {sl.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Specific storage location within the plant</p>
                    {plantData.plant_id && plantStorageLocs.length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">No storage locations found for this plant</p>
                    )}
                  </div>
                </div>

                {/* Shelf Life Configuration */}
                <div className="mt-4 pt-4 border-t border-orange-300">
                  <h5 className="text-xs font-semibold text-gray-700 mb-2">Shelf Life Configuration</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Period Indicator for Shelf Life Expiration</Label>
                      <select
                        className={`mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${readOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                        value={plantData.period_ind_shelf_life || ''}
                        onChange={(e) => handlePlantFieldChange(index, 'period_ind_shelf_life', e.target.value)}
                        disabled={readOnly}
                      >
                        <option value="">Select Period (Optional)</option>
                        <option value="D">D - Days</option>
                        <option value="M">M - Months</option>
                        <option value="W">W - Weeks</option>
                        <option value="Y">Y - Years</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Unit of time for minimum remaining shelf life</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : !isNestedFormat ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <p className="text-gray-600 mb-2">No plant extensions added yet</p>
          <p className="text-sm text-gray-500">Click &quot;Add Plant&quot; button below to add plant data</p>
        </div>
      ) : null}

      {/* Add Plant Button - Only show when not viewing nested data and not in readOnly mode */}
      {!isNestedFormat && !readOnly && (
        <button
          type="button"
          onClick={handleAddPlant}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add Plant
        </button>
      )}

      {/* Info Note */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
        <p className="font-medium text-gray-900 mb-1">📋 Note:</p>
        <p>
          You can add multiple plants for this material. Each plant can have its own storage location and configuration.
          Material must be extended to specific plants to be used for any transactional activity 
          (Procurement, Production, Sales, Stock Management).
        </p>
      </div>
    </div>
  );
};

export default PlantDataStorageView;
